import { Form, Head } from "@inertiajs/react"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/app-layout"
import { agentPropertiesPath } from "@/routes"
import type { BreadcrumbItem, Property, Suite } from "@/types"

interface Props {
  property: Property
  suite: Suite
  statuses: string[]
}

const statusLabel: Record<string, string> = {
  available: "Available",
  under_negotiation: "Under Negotiation",
  leased: "Leased",
}

export default function Edit({ property, suite, statuses }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Properties", href: agentPropertiesPath() },
    { title: property.name, href: `/agent/properties/${property.id}` },
    { title: `Suite ${suite.suite_number}`, href: `/agent/properties/${property.id}/suites/${suite.id}/edit` },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit Suite ${suite.suite_number}`} />

      <div className="max-w-2xl space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold">Edit Suite {suite.suite_number}</h1>
          <p className="text-muted-foreground text-sm">{property.name}</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Suite Details</CardTitle>
          </CardHeader>
          <CardContent>
            <Form
              method="patch"
              action={`/agent/properties/${property.id}/suites/${suite.id}`}
              encType="multipart/form-data"
              className="space-y-4"
            >
              {({ errors, processing }) => (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="suite_number">Suite Number</Label>
                      <Input id="suite_number" name="suite_number" defaultValue={suite.suite_number} />
                      <InputError messages={errors.suite_number} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="floor">Floor</Label>
                      <Input id="floor" name="floor" type="number" defaultValue={suite.floor ?? ""} />
                      <InputError messages={errors.floor} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="sqft">Square Footage</Label>
                      <Input id="sqft" name="sqft" type="number" defaultValue={suite.sqft ?? ""} />
                      <InputError messages={errors.sqft} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="monthly_rent">Monthly Rent ($)</Label>
                      <Input id="monthly_rent" name="monthly_rent" type="number" defaultValue={suite.monthly_rent ?? ""} />
                      <InputError messages={errors.monthly_rent} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="available_on">Available On</Label>
                      <Input id="available_on" name="available_on" type="date" defaultValue={suite.available_on ?? ""} />
                      <InputError messages={errors.available_on} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="status">Status</Label>
                      <Select name="status" defaultValue={suite.status}>
                        <SelectTrigger id="status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statuses.map((s) => (
                            <SelectItem key={s} value={s}>
                              {statusLabel[s] ?? s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <InputError messages={errors.status} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="rate_per_sqft">Rate ($/SF/YR)</Label>
                      <Input
                        id="rate_per_sqft"
                        name="rate_per_sqft"
                        type="number"
                        step="0.01"
                        defaultValue={suite.rate_per_sqft ?? ""}
                        placeholder="25.00"
                      />
                      <InputError messages={errors.rate_per_sqft} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="lease_type">Lease Type</Label>
                      <Input id="lease_type" name="lease_type" defaultValue={suite.lease_type ?? "NNN"} placeholder="NNN / Modified Gross" />
                      <InputError messages={errors.lease_type} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="dimensions">Dimensions</Label>
                      <Input id="dimensions" name="dimensions" defaultValue={suite.dimensions ?? ""} placeholder="46' x 89'" />
                      <InputError messages={errors.dimensions} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="map_marker">Site Map Marker #</Label>
                      <Input id="map_marker" name="map_marker" type="number" defaultValue={suite.map_marker ?? ""} />
                      <InputError messages={errors.map_marker} />
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="available_label">Marketing Headline</Label>
                    <Input
                      id="available_label"
                      name="available_label"
                      defaultValue={suite.available_label ?? ""}
                      placeholder="Suite 5 Available"
                    />
                    <p className="text-muted-foreground text-xs">Shown in the dark callout band on the Marketing brochure.</p>
                    <InputError messages={errors.available_label} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="features">Bullet Features (one per line)</Label>
                    <Textarea
                      id="features"
                      name="features"
                      defaultValue={suite.features ?? ""}
                      rows={4}
                      placeholder={"Located in the front building of the shopping center which makes for maximum visibility\nCorner suite which opens into a large space\nIncludes a showroom, storage, service counter, bathroom, & more"}
                    />
                    <InputError messages={errors.features} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea id="description" name="description" defaultValue={suite.description ?? ""} rows={3} />
                    <InputError messages={errors.description} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="photos">Add More Photos</Label>
                    <Input id="photos" name="photos[]" type="file" multiple accept="image/*" />
                    <InputError messages={errors.photos} />
                  </div>

                  {suite.photo_urls && suite.photo_urls.length > 0 && (
                    <div className="grid gap-2">
                      <Label>Current Photos</Label>
                      <div className="flex flex-wrap gap-2">
                        {suite.photo_urls.map((url, i) => (
                          <img key={i} src={url} alt={`Photo ${i + 1}`} loading="lazy" className="h-20 w-20 rounded object-cover" />
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <Button type="submit" disabled={processing}>Save Changes</Button>
                    <Button variant="outline" type="button" onClick={() => history.back()}>
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </Form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
